import { useEffect, useRef, useState } from 'react';
import { apiFetch } from '../../lib/api';
import { uploadProductImage } from '../../lib/upload';

/**
 * Edit homepage media + copy. Stored as key/value rows in `site_content`.
 * Image uploads reuse the existing product-images bucket.
 */
interface HeroValue {
  image_url?: string;
  video_url?: string | null;
  eyebrow?: string;
  headline?: string;
  subheadline?: string;
}
interface JoinValue {
  image_url?: string;
  label?: string;
  headline?: string;
  body?: string;
}
interface SiteContentRow {
  key: string;
  value: HeroValue | JoinValue | Record<string, unknown>;
  updated_at: string;
}

const HERO_KEY = 'homepage_hero';
const JOIN_KEY = 'homepage_join_card';
const LOGO_KEY = 'nav_logo';
const PDP_RETURNS_KEY = 'pdp_returns_rotation';
const PDP_DAMAGE_KEY = 'pdp_damage_loss';
const FAQ_KEY = 'faq_items';

interface LogoValue {
  image_url?: string;
}
interface AccordionTextValue {
  body?: string;
}
export interface FaqItemRow {
  question: string;
  answer: string;
  category?: string;
}
interface FaqValue {
  items?: FaqItemRow[];
}

interface PolicySlot {
  key: string;
  title: string;
  description: string;
}
const POLICY_SLOTS: PolicySlot[] = [
  // Note: How It Works is intentionally not in this list — its layout is a
  // bespoke marketing page rather than a parsed-docx legal doc.
  {
    key: 'policy_returns',
    title: 'Returns & Rental Policy',
    description: 'Shown at /returns.'
  },
  {
    key: 'policy_returns_damage',
    title: 'Customer Returns & Damage',
    description: 'Combined customer-facing returns + damage explainer.'
  },
  {
    key: 'policy_damage_loss',
    title: 'Damage & Loss Policy',
    description: 'Shown at /damage-loss.'
  },
  {
    key: 'policy_terms',
    title: 'Terms & Conditions',
    description: 'Shown at /terms.'
  },
  {
    key: 'policy_cookies',
    title: 'Cookie Policy',
    description: 'Shown at /cookies.'
  }
];

interface PolicyValue {
  html?: string;
  source_filename?: string | null;
  updated_at?: string;
}

export function AdminSiteContent() {
  const [hero, setHero] = useState<HeroValue>({});
  const [join, setJoin] = useState<JoinValue>({});
  const [logo, setLogo] = useState<LogoValue>({});
  const [pdpReturns, setPdpReturns] = useState<AccordionTextValue>({});
  const [pdpDamage, setPdpDamage] = useState<AccordionTextValue>({});
  const [faqItems, setFaqItems] = useState<FaqItemRow[]>([]);
  const [policies, setPolicies] = useState<Record<string, PolicyValue>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  function load() {
    setLoading(true);
    apiFetch<SiteContentRow[]>('/api/admin/site-content')
      .then((rows) => {
        const nextPolicies: Record<string, PolicyValue> = {};
        for (const r of rows) {
          if (r.key === HERO_KEY) setHero(r.value as HeroValue);
          else if (r.key === JOIN_KEY) setJoin(r.value as JoinValue);
          else if (r.key === LOGO_KEY) setLogo(r.value as LogoValue);
          else if (r.key === PDP_RETURNS_KEY) setPdpReturns(r.value as AccordionTextValue);
          else if (r.key === PDP_DAMAGE_KEY) setPdpDamage(r.value as AccordionTextValue);
          else if (r.key === FAQ_KEY) {
            const v = r.value as FaqValue;
            setFaqItems(Array.isArray(v.items) ? v.items : []);
          }
          else if (r.key.startsWith('policy_')) {
            nextPolicies[r.key] = r.value as PolicyValue;
          }
        }
        setPolicies(nextPolicies);
      })
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load'))
      .finally(() => setLoading(false));
  }
  useEffect(load, []);

  async function savePolicy(key: string, value: PolicyValue) {
    setSaving(key);
    setError(null);
    try {
      await apiFetch(`/api/admin/site-content/${key}`, {
        method: 'PATCH',
        body: JSON.stringify({ value })
      });
      setPolicies((p) => ({ ...p, [key]: value }));
      setSavedAt(new Date().toLocaleTimeString());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(null);
    }
  }

  async function save(key: string, value: unknown, label: string) {
    setSaving(label);
    setError(null);
    try {
      await apiFetch(`/api/admin/site-content/${key}`, {
        method: 'PATCH',
        body: JSON.stringify({ value })
      });
      setSavedAt(new Date().toLocaleTimeString());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(null);
    }
  }

  return (
    <>
      <div className="admin-page-head">
        <div>
          <h1>Site content</h1>
          <div className="sub">Homepage media and copy. Changes go live immediately.</div>
        </div>
        {savedAt && (
          <div style={{ fontSize: 12, color: 'var(--a-good)', alignSelf: 'center' }}>
            Saved at {savedAt}
          </div>
        )}
      </div>

      {error && <div className="a-alert a-alert-error">{error}</div>}
      {loading && <div style={{ padding: 16, color: 'var(--a-ink-soft)' }}>Loading…</div>}

      {!loading && (
        <div style={{ display: 'grid', gap: 18 }}>
          {/* NAV LOGO */}
          <div className="a-card">
            <div className="a-card-head">
              <h2>Nav bar logo</h2>
              <span style={{ fontSize: 11, color: 'var(--a-muted)' }}>
                Replaces the LABEL ROTATION wordmark in the header. PNG or SVG, transparent background recommended.
              </span>
            </div>
            <div className="a-card-body">
              <div style={{ display: 'grid', gridTemplateColumns: '220px 1fr', gap: 18, alignItems: 'flex-start' }}>
                <div>
                  <MediaTile
                    url={logo.image_url}
                    onChange={(url) => setLogo({ image_url: url ?? '' })}
                    accept="image/png,image/svg+xml"
                  />
                  <div className="help" style={{ marginTop: 6 }}>
                    Renders at ~40px on mobile, ~52px on desktop. PNG with transparent
                    background or SVG works best.
                  </div>
                </div>
                <div
                  style={{
                    border: '1px solid var(--a-line)',
                    borderRadius: 'var(--a-radius)',
                    padding: 16,
                    background: '#f4efe6',
                    minHeight: 90,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 14
                  }}
                >
                  <div
                    style={{
                      fontSize: 11,
                      letterSpacing: '0.14em',
                      color: 'var(--a-muted)',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      flexShrink: 0
                    }}
                  >
                    Preview
                  </div>
                  {logo.image_url ? (
                    <img
                      src={logo.image_url}
                      alt="Nav logo"
                      style={{ maxHeight: 52, maxWidth: 300, objectFit: 'contain' }}
                    />
                  ) : (
                    <span
                      style={{
                        fontFamily: "'Archivo Black', sans-serif",
                        fontSize: 15,
                        letterSpacing: '0.1em',
                        color: '#0e0e0e'
                      }}
                    >
                      LABEL ROTATION
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="a-form-foot">
              {logo.image_url && (
                <button
                  type="button"
                  className="btn-ghost"
                  onClick={() => {
                    setLogo({ image_url: '' });
                  }}
                  style={{ marginRight: 'auto' }}
                >
                  Remove logo (revert to wordmark)
                </button>
              )}
              <button
                className="btn-primary"
                onClick={() => void save(LOGO_KEY, logo, 'logo')}
                disabled={saving === 'logo'}
              >
                {saving === 'logo' ? 'Saving…' : 'Save logo'}
              </button>
            </div>
          </div>

          {/* HERO */}
          <div className="a-card">
            <div className="a-card-head">
              <h2>Homepage hero</h2>
              <span style={{ fontSize: 11, color: 'var(--a-muted)' }}>
                The big banner at the top of the homepage.
              </span>
            </div>
            <div className="a-card-body">
              <div className="a-form-grid" style={{ alignItems: 'flex-start' }}>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8 }}>
                    Image
                  </div>
                  <MediaTile
                    url={hero.image_url}
                    onChange={(url) => setHero((h) => ({ ...h, image_url: url ?? '' }))}
                    accept="image/*"
                  />
                  <div className="help" style={{ marginTop: 6 }}>
                    Recommended 1200×900. Used as the hero background.
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8 }}>
                    Video (optional)
                  </div>
                  <MediaTile
                    url={hero.video_url ?? undefined}
                    onChange={(url) => setHero((h) => ({ ...h, video_url: url ?? null }))}
                    accept="video/*"
                    isVideo
                  />
                  <div className="help" style={{ marginTop: 6 }}>
                    MP4 only, &lt; 10MB. Plays muted/looped over the hero image.
                  </div>
                </div>
              </div>
              <div className="a-form-grid" style={{ marginTop: 18 }}>
                <div className="a-field">
                  <label>Eyebrow</label>
                  <input
                    type="text"
                    value={hero.eyebrow ?? ''}
                    onChange={(e) => setHero((h) => ({ ...h, eyebrow: e.target.value }))}
                    placeholder="A NEW WAY TO DRESS"
                  />
                </div>
                <div className="a-field">
                  <label>Subheadline</label>
                  <input
                    type="text"
                    value={hero.subheadline ?? ''}
                    onChange={(e) => setHero((h) => ({ ...h, subheadline: e.target.value }))}
                    placeholder="ZERO COMMITMENT."
                  />
                </div>
              </div>
              <div className="a-field" style={{ marginTop: 14 }}>
                <label>Headline (use \n for line breaks)</label>
                <textarea
                  value={hero.headline ?? ''}
                  onChange={(e) => setHero((h) => ({ ...h, headline: e.target.value }))}
                  placeholder={'YOUR\\nWARDROBE.\\nON ROTATION.'}
                  rows={3}
                />
                <div className="help">
                  Type a backslash-n where you want a line break, e.g. <code>YOUR\nWARDROBE.</code>
                </div>
              </div>
            </div>
            <div className="a-form-foot">
              <button
                className="btn-primary"
                onClick={() => void save(HERO_KEY, hero, 'hero')}
                disabled={saving === 'hero'}
              >
                {saving === 'hero' ? 'Saving…' : 'Save hero'}
              </button>
            </div>
          </div>

          {/* JOIN CARD */}
          <div className="a-card">
            <div className="a-card-head">
              <h2>Join card</h2>
              <span style={{ fontSize: 11, color: 'var(--a-muted)' }}>
                The “A smarter way to dress” banner near the bottom.
              </span>
            </div>
            <div className="a-card-body">
              <div className="a-form-grid" style={{ alignItems: 'flex-start' }}>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8 }}>
                    Background image
                  </div>
                  <MediaTile
                    url={join.image_url}
                    onChange={(url) => setJoin((j) => ({ ...j, image_url: url ?? '' }))}
                    accept="image/*"
                  />
                </div>
                <div className="a-form" style={{ alignSelf: 'stretch' }}>
                  <div className="a-field">
                    <label>Label</label>
                    <input
                      type="text"
                      value={join.label ?? ''}
                      onChange={(e) => setJoin((j) => ({ ...j, label: e.target.value }))}
                      placeholder="JOIN THE ROTATION"
                    />
                  </div>
                  <div className="a-field">
                    <label>Headline (use \n for line breaks)</label>
                    <input
                      type="text"
                      value={join.headline ?? ''}
                      onChange={(e) => setJoin((j) => ({ ...j, headline: e.target.value }))}
                      placeholder={'A SMARTER WAY\\nTO DRESS.'}
                    />
                  </div>
                  <div className="a-field">
                    <label>Body</label>
                    <textarea
                      value={join.body ?? ''}
                      onChange={(e) => setJoin((j) => ({ ...j, body: e.target.value }))}
                      placeholder="More style. Less hassle. A wardrobe that moves with you."
                      rows={3}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="a-form-foot">
              <button
                className="btn-primary"
                onClick={() => void save(JOIN_KEY, join, 'join')}
                disabled={saving === 'join'}
              >
                {saving === 'join' ? 'Saving…' : 'Save join card'}
              </button>
            </div>
          </div>

          {/* PRODUCT PAGE ACCORDIONS — edit once, applied to every product */}
          <div className="a-card">
            <div className="a-card-head">
              <h2>Product page accordions</h2>
              <span style={{ fontSize: 11, color: 'var(--a-muted)' }}>
                The “Returns &amp; rotation period” and “Damage &amp; loss” copy that
                appears on every product page. Edit here once.
              </span>
            </div>
            <div className="a-card-body">
              <div className="a-field">
                <label>Returns &amp; rotation period</label>
                <textarea
                  value={pdpReturns.body ?? ''}
                  onChange={(e) => setPdpReturns({ body: e.target.value })}
                  rows={5}
                  placeholder="Your rotation lasts 30 days from delivery. We email a prepaid return label between Day 25–30. A 2-day grace period applies. Late fees of £10 per day apply from Day 33."
                />
              </div>
              <div className="a-field" style={{ marginTop: 14 }}>
                <label>Damage &amp; loss</label>
                <textarea
                  value={pdpDamage.body ?? ''}
                  onChange={(e) => setPdpDamage({ body: e.target.value })}
                  rows={5}
                  placeholder="Normal wear is expected and never charged. A damage fee only applies if the item cannot be returned to the rotation. The damage fee for this item is shown above."
                />
                <div className="help">
                  Tip: write {'{damage_fee}'} where you want the per-item £
                  amount substituted in (e.g. <code>The damage fee is £{'{damage_fee}'}.</code>).
                </div>
              </div>
            </div>
            <div className="a-form-foot">
              <button
                className="btn-primary"
                onClick={() => {
                  void save(PDP_RETURNS_KEY, pdpReturns, 'pdp_returns');
                  void save(PDP_DAMAGE_KEY, pdpDamage, 'pdp_damage');
                }}
                disabled={saving === 'pdp_returns' || saving === 'pdp_damage'}
              >
                {saving === 'pdp_returns' || saving === 'pdp_damage'
                  ? 'Saving…'
                  : 'Save accordion text'}
              </button>
            </div>
          </div>

          {/* FAQ — admin CRUD over a JSON array stored under faq_items */}
          <div className="a-card">
            <div className="a-card-head">
              <h2>Frequently asked questions</h2>
              <span style={{ fontSize: 11, color: 'var(--a-muted)' }}>
                Powers /faq. Group questions into categories so the page stays scannable.
              </span>
            </div>
            <div className="a-card-body" style={{ display: 'grid', gap: 10 }}>
              {faqItems.length === 0 && (
                <div
                  style={{
                    fontSize: 13,
                    color: 'var(--a-ink-soft)',
                    padding: '14px 16px',
                    border: '1px dashed var(--a-line)',
                    borderRadius: 'var(--a-radius)',
                    background: 'var(--a-bg)'
                  }}
                >
                  No FAQs yet. Add your first below.
                </div>
              )}
              {faqItems.map((item, i) => (
                <FaqRow
                  key={i}
                  item={item}
                  onChange={(next) => {
                    setFaqItems((all) => {
                      const copy = [...all];
                      copy[i] = next;
                      return copy;
                    });
                  }}
                  onDelete={() => {
                    setFaqItems((all) => all.filter((_, j) => j !== i));
                  }}
                  onMove={(dir) => {
                    setFaqItems((all) => {
                      const j = i + (dir === 'up' ? -1 : 1);
                      if (j < 0 || j >= all.length) return all;
                      const copy = [...all];
                      [copy[i], copy[j]] = [copy[j], copy[i]];
                      return copy;
                    });
                  }}
                  isFirst={i === 0}
                  isLast={i === faqItems.length - 1}
                />
              ))}
              <button
                type="button"
                className="btn-secondary"
                onClick={() =>
                  setFaqItems((all) => [...all, { question: '', answer: '', category: '' }])
                }
                style={{ alignSelf: 'flex-start' }}
              >
                + Add question
              </button>
            </div>
            <div className="a-form-foot">
              <button
                className="btn-primary"
                onClick={() => void save(FAQ_KEY, { items: faqItems }, 'faqs')}
                disabled={saving === 'faqs'}
              >
                {saving === 'faqs' ? 'Saving…' : 'Save FAQs'}
              </button>
            </div>
          </div>

          {/* POLICY DOCS — upload .docx, parse to HTML, save to site_content */}
          <div className="a-card">
            <div className="a-card-head">
              <h2>Policy documents</h2>
              <span style={{ fontSize: 11, color: 'var(--a-muted)' }}>
                Upload a Word doc — we&rsquo;ll parse and publish it.
              </span>
            </div>
            <div className="a-card-body" style={{ display: 'grid', gap: 14 }}>
              {POLICY_SLOTS.map((slot) => (
                <PolicyEditor
                  key={slot.key}
                  slot={slot}
                  value={policies[slot.key] ?? {}}
                  saving={saving === slot.key}
                  onSave={(v) => void savePolicy(slot.key, v)}
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function MediaTile({
  url,
  onChange,
  accept,
  isVideo
}: {
  url?: string;
  onChange: (url: string | null) => void;
  accept: string;
  isVideo?: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function handleFile(file: File) {
    setUploading(true);
    setErr(null);
    try {
      const publicUrl = await uploadProductImage(file);
      onChange(publicUrl);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  }

  return (
    <div>
      <div
        className={`upload-tile${url ? ' has-image' : ''}${uploading ? ' uploading' : ''}`}
        style={
          url && !isVideo
            ? { backgroundImage: `url(${url})`, aspectRatio: '16/10' }
            : { aspectRatio: '16/10' }
        }
        onClick={() => !url && !uploading && inputRef.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          if (url || uploading) return;
          const f = e.dataTransfer.files[0];
          if (f) void handleFile(f);
        }}
      >
        {!url && !uploading && (
          <>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
              <polyline points="17 8 12 3 7 8" />
              <line x1="12" y1="3" x2="12" y2="15" />
            </svg>
            <span>Click or drop {isVideo ? 'video' : 'image'}</span>
          </>
        )}
        {url && isVideo && (
          <video
            src={url}
            autoPlay
            loop
            muted
            playsInline
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        )}
        {uploading && <div className="uploading-label">Uploading…</div>}
        {url && !uploading && (
          <div className="actions">
            <button
              type="button"
              className="ic-btn"
              onClick={(e) => {
                e.stopPropagation();
                inputRef.current?.click();
              }}
              aria-label="Replace"
              title="Replace"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                <polyline points="17 8 12 3 7 8" />
                <line x1="12" y1="3" x2="12" y2="15" />
              </svg>
            </button>
            <button
              type="button"
              className="ic-btn danger"
              onClick={(e) => {
                e.stopPropagation();
                onChange(null);
              }}
              aria-label="Remove"
              title="Remove"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M6 6l12 12M18 6L6 18" />
              </svg>
            </button>
          </div>
        )}
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          style={{ display: 'none' }}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void handleFile(f);
            e.target.value = '';
          }}
        />
      </div>
      {err && <div style={{ fontSize: 11, color: 'var(--a-danger)', marginTop: 4 }}>{err}</div>}
    </div>
  );
}

/**
 * Single policy slot editor. Drag a .docx in, browser parses it via mammoth,
 * shows a live HTML preview, then saves to site_content on confirm.
 */
function PolicyEditor({
  slot,
  value,
  saving,
  onSave
}: {
  slot: PolicySlot;
  value: PolicyValue;
  saving: boolean;
  onSave: (v: PolicyValue) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [draft, setDraft] = useState<PolicyValue | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [parsing, setParsing] = useState(false);
  const [warnings, setWarnings] = useState<string[]>([]);

  async function handleFile(file: File) {
    setError(null);
    setWarnings([]);
    setParsing(true);
    try {
      // Lazy-import mammoth so the bundle stays slim for non-admin pages.
      const { parseDocxToHtml } = await import('../../lib/docx');
      const { html, messages } = await parseDocxToHtml(file);
      setDraft({
        html,
        source_filename: file.name,
        updated_at: new Date().toISOString()
      });
      if (messages.length) setWarnings(messages);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Parse failed');
    } finally {
      setParsing(false);
    }
  }

  const current = draft ?? value;
  const hasContent = !!current.html;
  const isDirty = !!draft;

  return (
    <div
      style={{
        border: '1px solid var(--a-line)',
        borderRadius: 'var(--a-radius)',
        padding: 14,
        background: 'var(--a-card)'
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 12,
          marginBottom: 8
        }}
      >
        <div>
          <div style={{ fontWeight: 700, fontSize: 14 }}>{slot.title}</div>
          <div style={{ fontSize: 11, color: 'var(--a-muted)', marginTop: 2 }}>
            {slot.description}
          </div>
          {current.source_filename && (
            <div
              style={{
                fontSize: 11,
                color: 'var(--a-ink-soft)',
                marginTop: 4,
                fontFamily: 'JetBrains Mono, ui-monospace, monospace'
              }}
            >
              {current.source_filename}
              {current.updated_at && (
                <span style={{ color: 'var(--a-muted)' }}>
                  {' '}· updated {new Date(current.updated_at).toLocaleDateString('en-GB')}
                </span>
              )}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
          <button
            type="button"
            className="btn-secondary"
            onClick={() => inputRef.current?.click()}
            disabled={parsing}
          >
            {parsing ? 'Parsing…' : hasContent ? 'Replace .docx' : 'Upload .docx'}
          </button>
          {isDirty && draft && (
            <button
              type="button"
              className="btn-primary"
              onClick={() => onSave(draft)}
              disabled={saving}
            >
              {saving ? 'Publishing…' : 'Publish'}
            </button>
          )}
          {isDirty && (
            <button
              type="button"
              className="btn-ghost"
              onClick={() => {
                setDraft(null);
                setWarnings([]);
              }}
            >
              Discard
            </button>
          )}
        </div>
      </div>

      <input
        ref={inputRef}
        type="file"
        accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        style={{ display: 'none' }}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void handleFile(f);
          e.target.value = '';
        }}
      />

      {error && (
        <div className="a-alert a-alert-error" style={{ marginTop: 8 }}>
          {error}
        </div>
      )}

      {warnings.length > 0 && (
        <details style={{ marginTop: 8 }}>
          <summary style={{ fontSize: 12, color: 'var(--a-warn)', cursor: 'pointer' }}>
            {warnings.length} formatting note{warnings.length === 1 ? '' : 's'} from the parser
          </summary>
          <ul style={{ fontSize: 11, color: 'var(--a-ink-soft)', margin: '6px 0 0 16px' }}>
            {warnings.slice(0, 6).map((m, i) => (
              <li key={i}>{m}</li>
            ))}
            {warnings.length > 6 && <li>… and {warnings.length - 6} more</li>}
          </ul>
        </details>
      )}

      {hasContent && (
        <details
          open={isDirty}
          style={{
            marginTop: 10,
            border: '1px solid var(--a-line-soft)',
            borderRadius: 6,
            padding: '8px 10px',
            background: 'var(--a-bg)'
          }}
        >
          <summary style={{ fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
            {isDirty ? 'Preview (not yet published)' : 'Preview'}
          </summary>
          <div
            // We trust mammoth's output — it strips Word's vendor styling and
            // emits clean tagged HTML. No <script> can survive its converter.
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: current.html ?? '' }}
            style={{
              marginTop: 10,
              fontSize: 13,
              lineHeight: 1.55,
              color: 'var(--a-ink)',
              maxHeight: 320,
              overflowY: 'auto',
              padding: 8
            }}
          />
        </details>
      )}

      {!hasContent && (
        <div
          style={{
            marginTop: 8,
            fontSize: 12,
            color: 'var(--a-muted)',
            background: 'var(--a-bg)',
            padding: '10px 12px',
            borderRadius: 6,
            border: '1px dashed var(--a-line)'
          }}
        >
          No document uploaded yet. The site shows the built-in fallback copy until you publish one.
        </div>
      )}
    </div>
  );
}

function FaqRow({
  item,
  onChange,
  onDelete,
  onMove,
  isFirst,
  isLast
}: {
  item: FaqItemRow;
  onChange: (next: FaqItemRow) => void;
  onDelete: () => void;
  onMove: (dir: 'up' | 'down') => void;
  isFirst: boolean;
  isLast: boolean;
}) {
  return (
    <div
      style={{
        border: '1px solid var(--a-line)',
        borderRadius: 'var(--a-radius)',
        padding: 14,
        background: 'var(--a-card)'
      }}
    >
      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 10 }}>
        <div className="a-field" style={{ gap: 4 }}>
          <label>Category</label>
          <input
            type="text"
            value={item.category ?? ''}
            onChange={(e) => onChange({ ...item, category: e.target.value })}
            placeholder="e.g. Credits & Payments"
          />
        </div>
        <div style={{ display: 'flex', gap: 4, alignItems: 'flex-end' }}>
          <button
            type="button"
            className="btn-ghost"
            onClick={() => onMove('up')}
            disabled={isFirst}
            aria-label="Move up"
            title="Move up"
          >
            ↑
          </button>
          <button
            type="button"
            className="btn-ghost"
            onClick={() => onMove('down')}
            disabled={isLast}
            aria-label="Move down"
            title="Move down"
          >
            ↓
          </button>
          <button
            type="button"
            className="btn-ghost"
            onClick={onDelete}
            style={{ color: 'var(--a-danger)' }}
          >
            Delete
          </button>
        </div>
      </div>
      <div className="a-field" style={{ gap: 4, marginTop: 10 }}>
        <label>Question</label>
        <input
          type="text"
          value={item.question}
          onChange={(e) => onChange({ ...item, question: e.target.value })}
          placeholder="Do my credits expire?"
        />
      </div>
      <div className="a-field" style={{ gap: 4, marginTop: 10 }}>
        <label>Answer</label>
        <textarea
          rows={3}
          value={item.answer}
          onChange={(e) => onChange({ ...item, answer: e.target.value })}
          placeholder="No. Credits sit in your account until you're ready to use them."
        />
      </div>
    </div>
  );
}
